import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "./Login";
import Register from "./Register";
import Reset from "./Reset";
import Dashboard from "./Dashboard";
import Profile from "./Profile";
import MyReservations from "./MyReservations";
import "./App.css";
import "./custom-bootstrap.css";
import TeeTimes from "./TeeTimes";
import GroupChecker from "./GroupChecker";
import GroupLoginLanding from "./GroupLoginLanding";
import LogOut from "./LogOut";
import { useEffect, useState } from "react";
import { db, auth } from "./config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { Course } from "./types/firebase";
import { checkAndSetAppMode } from "./utils/appMode";
import AppUpdateBanner from "./components/AppUpdateBanner";
import { useAppUpdateCheck } from "./hooks/useAppUpdateCheck";

console.log('App.tsx is being imported');

declare global {
  interface Window {
    _mfq: any;
  }
}

function App() {
  console.log('App component is rendering');

  const [pageTitle, setPageTitle] = useState('My Golf Course App');
  const [course, setCourse] = useState<Course | null>(null);
  const groupID = localStorage.getItem("group");
  const courseID = localStorage.getItem("course");
  const { updateAvailable, dismissUpdate, applyUpdate, isAppMode: appModeActive } =
    useAppUpdateCheck();

  // Check for app mode on initial load
  useEffect(() => {
    checkAndSetAppMode();
  }, []);

  /** Defer Mouseflow until after first paint — reduces contention in embedded WebViews. */
  useEffect(() => {
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          window._mfq = window._mfq || [];
          const mf = document.createElement('script');
          mf.type = 'text/javascript';
          mf.defer = true;
          mf.src = '//cdn.mouseflow.com/projects/7e8017fc-b1c7-42fd-9225-1f470535ce83.js';
          document.head.appendChild(mf);
          console.log('[TRWidget] Mouseflow script scheduled');
        } catch (e) {
          console.warn('[TRWidget] Mouseflow inject failed', e);
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  console.log('App component state:', { groupID, courseID, hasCourse: !!course });

  useEffect(() => {
    console.log('App useEffect - course changed:', course);
    if (course?.name) {
      setPageTitle(course.name + " Reservations");
    }
    document.title = pageTitle;
    //eslint-disable-next-line
  }, [course])

  useEffect(() => {
    console.log('App useEffect - fetching course data:', { groupID, courseID });

    if (groupID && courseID) {
      const fetchCourse = async () => {
        try {
          if (!auth.currentUser) {
            await signInAnonymously(auth);
          }
          const courseRef = doc(db, 'groups', groupID, 'courses', courseID);
          const docSnap = await getDoc(courseRef);
          console.log('Course doc exists:', docSnap.exists());
          if (docSnap.exists()) {
            setCourse(docSnap.data() as Course);
          } else {
            console.log('Course not found');
            setCourse(null);
          }
        } catch (err) {
          console.error("Error fetching course:", err);
          setCourse(null);
        }
      };
      void fetchCourse();
    } else {
      setCourse(null);
    }
  }, [courseID, groupID]);

  return (
    <div className="app appContainer">
      <AppUpdateBanner
        show={updateAvailable}
        isAppMode={appModeActive}
        onRefresh={applyUpdate}
        onDismiss={dismissUpdate}
      />
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/page/login" element={<Navigate to="/" replace />} />
          <Route path="/page/register" element={<Register />} />
          <Route path="/page/reset" element={<Reset />} />
          <Route path="/page/dashboard" element={<Dashboard />} />
          <Route path="/page/teetimes" element={<TeeTimes />} />
          <Route path="/page/profile" element={<Profile />} />
          <Route path="/page/reservations" element={<MyReservations />} />
          <Route path="/group/:group/course/:course/" element={<GroupChecker />} />
          <Route path="/group/:group" element={<GroupLoginLanding />} />
          <Route path="/logout/group/:group/course/:course/" element={<LogOut />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
